//
// V2 design language theme for the redesigned public surfaces (landing, signup).
// Intentionally a standalone createTheme() call — NOT a spread of the legacy
// baseTheme — to prevent legacy overrides (button border, popover radius 0,
// 0.8125rem body text, etc.) from leaking into the new design.
//
// Mounted only via <ThemeProviderV2> on v2 pages. Existing screens are untouched.
//
// NOTE: `palette.v2.{indigo,neutral,surface,gradients,shadows}` is a non-standard
// custom palette branch — read directly via `(theme) => theme.palette.v2.…` in
// `sx`. Do NOT pass `'v2.indigo.500'` to MUI helpers (`alpha()`, `getContrastText()`,
// etc.) that expect a standard palette path; they won't resolve it.
//
// The `surface.*` and `gradients.*` tokens come from the active variant in
// `themeVariants.js` — change `ACTIVE_VARIANT_NAME` there to flip between the
// Black/Yellow and Dark Blue visual identities.

import { createTheme } from '@mui/material/styles';
import { indigo, neutral, yellow, sharedSurface, shadowsV2 } from './palettes';
import { activeVariant } from './themeVariants';

const surface = {
  ...activeVariant.surface,
  ...sharedSurface
};

// Yellow CTA stays the brand accent in every variant.
const gradients = {
  ...activeVariant.gradients,
  cta: `linear-gradient(135deg, ${yellow[500]} 0%, ${yellow[700]} 100%)`
};

const themeV2 = createTheme({
  spacing: 8,
  palette: {
    mode: 'light',
    primary: {
      light: indigo[300],
      main: indigo[500],
      dark: indigo[700],
      contrastText: '#FFFFFF'
    },
    secondary: {
      light: yellow[300],
      main: yellow[500],
      dark: yellow[700],
      contrastText: '#0A0A0A'
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
      muted: neutral[50]
    },
    text: {
      primary: neutral[800],
      secondary: neutral[500]
    },
    divider: neutral[200],
    success: { main: '#22C55E' },
    error: { main: '#EF4444' },
    warning: { main: '#F59E0B' },
    info: { main: '#3B82F6' },
    v2: {
      indigo,
      neutral,
      surface,
      yellow,
      gradients,
      shadows: shadowsV2
    }
  },
  shape: {
    borderRadius: 14
  },
  typography: {
    fontFamily: ['Product Sans', 'system-ui', '-apple-system', 'Segoe UI', 'sans-serif'].join(','),
    h1: {
      fontSize: '3.75rem',
      fontWeight: 700,
      lineHeight: 1.08,
      letterSpacing: '-0.025em'
    },
    h2: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.15,
      letterSpacing: '-0.02em'
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: '-0.01em'
    },
    h4: {
      fontSize: '1.375rem',
      fontWeight: 700,
      lineHeight: 1.3
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.4
    },
    subtitle1: {
      fontSize: '1.25rem',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: 0
    },
    subtitle2: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6
    },
    body2: {
      fontSize: '0.9375rem',
      fontWeight: 400,
      lineHeight: 1.6
    },
    button: {
      fontSize: '1rem',
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: 0
    },
    caption: {
      fontSize: '0.8125rem',
      fontWeight: 400,
      lineHeight: 1.5,
      color: neutral[500]
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 1.5,
      textTransform: 'uppercase',
      letterSpacing: '0.08em'
    }
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: false
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: 0,
          paddingTop: 12,
          paddingBottom: 12,
          paddingLeft: 24,
          paddingRight: 24
        },
        sizeLarge: {
          fontSize: '1.0625rem',
          paddingTop: 16,
          paddingBottom: 16,
          paddingLeft: 28,
          paddingRight: 28
        },
        sizeSmall: {
          fontSize: '0.875rem',
          paddingTop: 8,
          paddingBottom: 8,
          paddingLeft: 16,
          paddingRight: 16
        },
        containedPrimary: {
          boxShadow: shadowsV2.primary,
          '&:hover': {
            boxShadow: '0 14px 32px rgba(92, 107, 192, 0.4)',
            transform: 'translateY(-1px)'
          },
          transition: 'transform 160ms ease, box-shadow 160ms ease'
        },
        containedSecondary: {
          // Yellow CTA stays the brand accent across every variant (see
          // themeVariants.js), so the yellow-tinted box shadow is intentional
          // and not variant-driven.
          backgroundColor: yellow[500],
          color: '#0A0A0A',
          boxShadow: '0 10px 24px rgba(255, 235, 59, 0.32)',
          '&:hover': {
            backgroundColor: yellow[400],
            boxShadow: '0 14px 32px rgba(255, 235, 59, 0.4)',
            transform: 'translateY(-1px)'
          },
          transition: 'transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease'
        },
        outlinedPrimary: {
          borderWidth: 1.5,
          '&:hover': {
            borderWidth: 1.5
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 14
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#FFFFFF',
          '& fieldset': {
            borderColor: neutral[200]
          },
          '&:hover fieldset': {
            borderColor: neutral[300]
          },
          '&.Mui-focused fieldset': {
            borderWidth: 1.5
          }
        },
        input: {
          paddingTop: 18,
          paddingBottom: 18,
          fontSize: '1rem'
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          color: neutral[500]
        }
      }
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined'
      }
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline'
          }
        }
      }
    }
  }
});

export default themeV2;
